/**
 * "PROVE ONCE, SATISFY MANY" — the leverage panel.
 *
 * ---------------------------------------------------------------------------
 *  WHAT THIS IS FOR
 * ---------------------------------------------------------------------------
 * Drop it beside a framework question. It answers the one question a
 * compliance team never gets an answer to today: *if I do this, what else does
 * it buy me?*
 *
 *     Prove this once and it also satisfies NIS2 Art. 21(2)(c),
 *     DORA Art. 12(1)-(3) and ISO 27001 A.8.13.
 *
 * That sentence is the product. Everything else here exists to make it
 * trustworthy.
 *
 * ---------------------------------------------------------------------------
 *  WHY THERE ARE TWO SENTENCES AND NOT ONE
 * ---------------------------------------------------------------------------
 * A control fully satisfies some of the requirements it maps to and only
 * partly satisfies others — NIS2's 24-hour reporting deadline is not DORA's,
 * and neither is ISO's (ISO sets none). Collapsing both into one "also
 * satisfies" list would be the single most damaging thing this component could
 * do: a customer would read a green sentence, stop work, and discover the gap
 * during an audit.
 *
 * So FULL mappings get "also satisfies", PARTIAL mappings get a separate
 * "also contributes to … without fully satisfying them", and every partial
 * mapping's reason is available in the detail list. The two lists are never
 * merged, never summed into one count, and the partial sentence is never
 * hidden to make the panel look tidier.
 *
 * ---------------------------------------------------------------------------
 *  WIRING
 * ---------------------------------------------------------------------------
 * Self-contained: it fetches its own data and owns its own state, so it can be
 * dropped into the assessment screen with a single line and no plumbing:
 *
 *     <ControlLeverage questionId={question.id} />
 *
 * Pass `readOnly` where the record is sealed (a finalised submission).
 */

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ORG_CONTROL_STATUSES,
  type ControlCoverage,
  type OrgControlStatus,
} from '@opencomply/shared';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { Button, Card, ErrorNotice, Pill, Skeleton, type Tone } from './ui';
import { EvidencePanel } from './EvidencePanel';

// ---------------------------------------------------------------------------
//  Strings
//
//  Every string carries its English text inline, so this component renders
//  correctly before the locale files are updated and the merger has exactly
//  one object to copy from. Keys are the i18n keys; `{{x}}` is i18next
//  interpolation and `_one` / `_other` are its plural suffixes.
// ---------------------------------------------------------------------------

export const CONTROL_LEVERAGE_STRINGS = {
  'leverage.title': 'Prove once, satisfy many',
  'leverage.subtitle':
    'These controls bear on this question. Each one you can prove counts everywhere it is mapped.',
  'leverage.loadFailed': 'Could not load the cross-framework mapping.',
  'leverage.none': 'No cross-framework control is mapped to this question yet.',

  // The headline sentences
  'leverage.alsoSatisfies': 'Prove this once and it also satisfies {{list}}.',
  'leverage.alsoContributes':
    'It also contributes to {{list}} — but does not fully satisfy them.',
  'leverage.onlyHere':
    'This control is mapped only to this requirement, so proving it buys no coverage elsewhere.',
  'leverage.citation': '{{framework}} {{ref}}',

  // Counts
  'leverage.otherFrameworks_one': '{{count}} other framework',
  'leverage.otherFrameworks_other': '{{count}} other frameworks',
  'leverage.requirements_one': '{{count}} requirement',
  'leverage.requirements_other': '{{count}} requirements',
  'leverage.fullCount_one': '{{count}} fully covered',
  'leverage.fullCount_other': '{{count}} fully covered',
  'leverage.partialCount_one': '{{count}} partly covered',
  'leverage.partialCount_other': '{{count}} partly covered',
  'leverage.evidenceCount_one': '{{count}} item of evidence',
  'leverage.evidenceCount_other': '{{count}} items of evidence',

  // Coverage of the mapping onto THIS question
  'leverage.thisFull': 'Fully satisfies {{ref}}',
  'leverage.thisPartial': 'Partly satisfies {{ref}}',
  'leverage.whyPartial': 'Why only partly:',

  // Detail
  'leverage.showDetail': 'Show every requirement this covers',
  'leverage.hideDetail': 'Hide the detail',
  'leverage.coverageFull': 'Full',
  'leverage.coveragePartial': 'Partial',
  'leverage.coverageFullExplained':
    'Operating this control discharges the cited requirement.',
  'leverage.coveragePartialExplained':
    'This control contributes to the cited requirement but does not discharge it on its own.',
  'leverage.unlicensedNote':
    'Your organisation is not licensed for {{framework}}, so the requirement text is hidden. The citation is public and is shown.',
  'leverage.openCoverage': 'Open the full coverage view',

  // Status
  'leverage.status': 'Do you satisfy this control?',
  'leverage.statusHint':
    'Recorded once for the whole organisation. It applies to every requirement mapped to this control.',
  'leverage.status.NOT_ASSESSED': 'Not assessed yet',
  'leverage.status.SATISFIED': 'Satisfied',
  'leverage.status.PARTIAL': 'Partly satisfied',
  'leverage.status.NOT_SATISFIED': 'Not satisfied',
  'leverage.status.NOT_APPLICABLE': 'Not applicable',
  'leverage.statusSaved': 'Saved',
  'leverage.statusSaveFailed': 'Could not save that. Try again.',
  'leverage.reviewedAt': 'Last reviewed {{date}}',
  'leverage.notReviewed': 'Nobody has recorded a position on this control yet.',
  'leverage.satisfiedWithoutEvidence':
    'Marked satisfied with no evidence attached. An auditor will ask for the artefact, so attach it here — once — and it counts everywhere above.',
  'leverage.partialWarning':
    'A partly satisfied control does not discharge the requirements it fully covers above. They are still open.',

  // Evidence
  'leverage.evidence': 'Evidence for this control',
  'leverage.evidenceHint':
    'Attach the proof here rather than against each framework question. One upload, every mapping.',
} as const;

export type ControlLeverageKey = keyof typeof CONTROL_LEVERAGE_STRINGS;

/**
 * The keys that come in `_one` / `_other` pairs.
 *
 * i18next is handed the BASE key plus a `count`, and picks the suffixed form
 * itself using the reader's plural rules — which is the only way a language
 * with more than two plural categories gets the right sentence. The inline
 * English fallback has to be chosen here, because a `defaultValue` is a single
 * string and i18next cannot pluralise one it was not given.
 */
const PLURAL_KEYS = [
  'leverage.otherFrameworks',
  'leverage.requirements',
  'leverage.fullCount',
  'leverage.partialCount',
  'leverage.evidenceCount',
] as const;
export type ControlLeveragePluralKey = (typeof PLURAL_KEYS)[number];

// ---------------------------------------------------------------------------
//  Transport types (mirror modules/catalogue/service.ts)
// ---------------------------------------------------------------------------

interface ElsewhereMapping {
  frameworkKey: string;
  frameworkName: string;
  questionCode: string;
  ref: string;
  coverage: ControlCoverage;
  rationale: string | null;
}

interface LeverageControl {
  key: string;
  title: string;
  description: string | null;
  evidenceGuidance: string | null;
  domain: string | null;
  thisRequirement: { ref: string; coverage: ControlCoverage; rationale: string | null };
  elsewhere: ElsewhereMapping[];
  otherFrameworkCount: number;
  adoption: {
    organizationControlId: string | null;
    status: OrgControlStatus;
    reviewedAt: string | null;
    evidenceCount: number;
  };
}

interface LeverageResponse {
  questionId: string;
  questionCode: string;
  controls: LeverageControl[];
}

// ---------------------------------------------------------------------------
//  Presentation helpers
// ---------------------------------------------------------------------------

/**
 * Tones for the organisation's position on a control.
 *
 * NOT_ASSESSED is neutral, NOT critical, and that is deliberate: "we have not
 * looked yet" is a different statement from "we looked and we do not have it".
 * Colouring them the same would tell a new tenant their whole programme is
 * failing on day one, which is both wrong and demoralising.
 */
const STATUS_TONE: Record<OrgControlStatus, Tone> = {
  NOT_ASSESSED: 'neutral',
  SATISFIED: 'good',
  PARTIAL: 'warning',
  NOT_SATISFIED: 'critical',
  NOT_APPLICABLE: 'neutral',
};

/**
 * Join citations the way the reader's language does — "A, B and C" in English,
 * "A, B og C" in Danish, and so on.
 *
 * `Intl.ListFormat` is in every browser this app supports, but the fallback is
 * kept because a missing locale datum must not blank out the sentence that is
 * the entire point of this component.
 */
function joinList(parts: string[], locale: string): string {
  if (parts.length === 0) return '';
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(parts);
  } catch {
    return parts.join(', ');
  }
}

export function ControlLeverage({
  questionId,
  readOnly = false,
}: {
  questionId: string;
  /** True when the record is sealed, or the viewer may not change it. */
  readOnly?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const describeApiError = useApiError();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  /** Translate with the built-in English as the fallback. */
  const tx = useCallback(
    (key: ControlLeverageKey, options?: Record<string, unknown>) =>
      t(key, { defaultValue: CONTROL_LEVERAGE_STRINGS[key], ...options }),
    [t],
  );

  /** Plural-aware sibling of `tx`. See PLURAL_KEYS. */
  const txn = useCallback(
    (key: ControlLeveragePluralKey, count: number) =>
      t(key, {
        count,
        defaultValue:
          CONTROL_LEVERAGE_STRINGS[
            (count === 1 ? `${key}_one` : `${key}_other`) as ControlLeverageKey
          ],
      }),
    [t],
  );

  const locale = i18n.language;
  const canEvaluate = !readOnly && allows('control.evaluate');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalogue', 'leverage', questionId],
    queryFn: () => api.get<LeverageResponse>(`/catalogue/leverage${qs({ questionId })}`),
  });

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (isLoading) {
    return (
      <Card className="p-4 space-y-3">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-16 w-full" />
      </Card>
    );
  }

  if (isError) {
    return <ErrorNotice message={tx('leverage.loadFailed')} onRetry={() => void refetch()} />;
  }

  const controls = data?.controls ?? [];

  return (
    <section aria-labelledby={`leverage-h-${questionId}`} className="space-y-3">
      <div>
        <h3 id={`leverage-h-${questionId}`} className="text-sm font-semibold text-ink">
          {tx('leverage.title')}
        </h3>
        <p className="text-xs text-ink-muted mt-0.5">{tx('leverage.subtitle')}</p>
      </div>

      {controls.length === 0 ? (
        <p className="text-sm text-ink-muted">{tx('leverage.none')}</p>
      ) : (
        <ul className="space-y-3">
          {controls.map((control) => (
            <LeverageCard
              key={control.key}
              control={control}
              tx={tx}
              txn={txn}
              locale={locale}
              canEvaluate={canEvaluate}
              readOnly={readOnly}
              expanded={expanded.has(control.key)}
              onToggle={() => toggle(control.key)}
              describeApiError={describeApiError}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ['catalogue'] });
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
//  One control
// ---------------------------------------------------------------------------

function LeverageCard({
  control,
  tx,
  txn,
  locale,
  canEvaluate,
  readOnly,
  expanded,
  onToggle,
  describeApiError,
  onSaved,
}: {
  control: LeverageControl;
  tx: (key: ControlLeverageKey, options?: Record<string, unknown>) => string;
  txn: (key: ControlLeveragePluralKey, count: number) => string;
  locale: string;
  canEvaluate: boolean;
  readOnly: boolean;
  expanded: boolean;
  onToggle: () => void;
  describeApiError: (err: unknown) => string;
  onSaved: () => void;
}) {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [status, setStatus] = useState<OrgControlStatus>(control.adoption.status);

  const save = useMutation({
    mutationFn: (next: OrgControlStatus) =>
      api.patch<{ id: string; status: OrgControlStatus }>(
        `/catalogue/${encodeURIComponent(control.key)}/status`,
        { status: next },
      ),
    onSuccess: (_result, next) => {
      setStatus(next);
      setSaveError(null);
      onSaved();
    },
    onError: (err) => setSaveError(describeApiError(err) || tx('leverage.statusSaveFailed')),
  });

  const citation = (m: { frameworkName: string; ref: string }) =>
    tx('leverage.citation', { framework: m.frameworkName, ref: m.ref });

  const fullElsewhere = control.elsewhere.filter((m) => m.coverage === 'FULL');
  const partialElsewhere = control.elsewhere.filter((m) => m.coverage === 'PARTIAL');

  return (
    <li>
      <Card className="p-4 space-y-3">
        {/* --- Heading ------------------------------------------------------ */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">{control.title}</p>
            {control.domain && (
              <p className="text-xs text-ink-muted mt-0.5">{control.domain}</p>
            )}
          </div>
          <Pill tone={STATUS_TONE[status]}>{tx(`leverage.status.${status}`)}</Pill>
        </div>

        {/* --- How it bears on THIS question -------------------------------- */}
        <p className="text-xs text-ink-muted">
          {control.thisRequirement.coverage === 'FULL'
            ? tx('leverage.thisFull', { ref: control.thisRequirement.ref })
            : tx('leverage.thisPartial', { ref: control.thisRequirement.ref })}
        </p>
        {control.thisRequirement.coverage === 'PARTIAL' && control.thisRequirement.rationale && (
          <p className="text-xs text-ink-muted">
            <span className="font-medium text-ink">{tx('leverage.whyPartial')}</span>{' '}
            {control.thisRequirement.rationale}
          </p>
        )}

        {/* --- THE SENTENCE ------------------------------------------------- */}
        {control.elsewhere.length === 0 ? (
          <p className="text-sm text-ink-muted">{tx('leverage.onlyHere')}</p>
        ) : (
          <div className="rounded-md bg-surface-sunken p-3 space-y-1.5">
            {fullElsewhere.length > 0 && (
              <p className="text-sm text-ink">
                {tx('leverage.alsoSatisfies', {
                  list: joinList(fullElsewhere.map(citation), locale),
                })}
              </p>
            )}
            {/*
              Never folded into the sentence above, and never hidden. A partial
              mapping presented as full coverage is the failure mode this whole
              feature is designed around.
            */}
            {partialElsewhere.length > 0 && (
              <p className="text-sm text-ink-muted">
                {tx('leverage.alsoContributes', {
                  list: joinList(partialElsewhere.map(citation), locale),
                })}
              </p>
            )}
          </div>
        )}

        {/* --- Counts ------------------------------------------------------- */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <span>{txn('leverage.otherFrameworks', control.otherFrameworkCount)}</span>
          <span aria-hidden>·</span>
          <span>{txn('leverage.requirements', control.elsewhere.length + 1)}</span>
          {control.adoption.evidenceCount > 0 && (
            <>
              <span aria-hidden>·</span>
              <span>{txn('leverage.evidenceCount', control.adoption.evidenceCount)}</span>
            </>
          )}
        </div>

        {/* --- Honesty guards ---------------------------------------------- */}
        {status === 'SATISFIED' && control.adoption.evidenceCount === 0 && (
          <p className="text-xs text-warning bg-warning/10 rounded-md p-2">
            {tx('leverage.satisfiedWithoutEvidence')}
          </p>
        )}
        {status === 'PARTIAL' && fullElsewhere.length > 0 && (
          <p className="text-xs text-warning bg-warning/10 rounded-md p-2">
            {tx('leverage.partialWarning')}
          </p>
        )}

        {/* --- Status setter ----------------------------------------------- */}
        {canEvaluate && (
          <fieldset className="space-y-1.5">
            <legend className="oc-label">{tx('leverage.status')}</legend>
            <p className="text-xs text-ink-muted">{tx('leverage.statusHint')}</p>
            <div className="flex flex-wrap gap-1.5">
              {ORG_CONTROL_STATUSES.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={status === value}
                  disabled={save.isPending}
                  onClick={() => save.mutate(value)}
                  className={cn(
                    'oc-btn-ghost oc-btn-sm',
                    status === value && 'ring-2 ring-brand font-semibold',
                  )}
                >
                  {tx(`leverage.status.${value}`)}
                </button>
              ))}
            </div>
            {saveError && <p className="text-xs text-critical">{saveError}</p>}
          </fieldset>
        )}

        {control.adoption.reviewedAt ? (
          <p className="text-xs text-ink-muted">
            {tx('leverage.reviewedAt', {
              date: new Date(control.adoption.reviewedAt).toLocaleDateString(locale),
            })}
          </p>
        ) : (
          <p className="text-xs text-ink-muted">{tx('leverage.notReviewed')}</p>
        )}

        {/* --- Detail ------------------------------------------------------- */}
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? tx('leverage.hideDetail') : tx('leverage.showDetail')}
          </Button>
          <Link
            to={`/catalogue/${encodeURIComponent(control.key)}`}
            className="text-xs text-brand hover:underline"
          >
            {tx('leverage.openCoverage')}
          </Link>
        </div>

        {expanded && (
          <div className="space-y-2 border-t border-line pt-3">
            {control.description && (
              <p className="text-xs text-ink-muted">{control.description}</p>
            )}
            <ul className="space-y-1.5">
              {control.elsewhere.map((m) => (
                <li key={`${m.frameworkKey}-${m.questionCode}`} className="text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink">{citation(m)}</span>
                    <Pill tone={m.coverage === 'FULL' ? 'good' : 'warning'}>
                      {m.coverage === 'FULL'
                        ? tx('leverage.coverageFull')
                        : tx('leverage.coveragePartial')}
                    </Pill>
                    <span className="text-ink-muted">{m.questionCode}</span>
                  </div>
                  {m.coverage === 'PARTIAL' && m.rationale && (
                    <p className="text-ink-muted mt-0.5">
                      <span className="font-medium text-ink">{tx('leverage.whyPartial')}</span>{' '}
                      {m.rationale}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* --- Evidence ----------------------------------------------------- */}
        {/*
          Only once the organisation has a row for this control: evidence hangs
          off the adoption, which is what keeps it tenant-scoped. Recording any
          status creates that row, so the panel appears as soon as someone has
          an opinion.
        */}
        {control.adoption.organizationControlId && (
          <div className="border-t border-line pt-3 space-y-1.5">
            <p className="text-xs text-ink-muted">{tx('leverage.evidenceHint')}</p>
            {control.evidenceGuidance && (
              <p className="text-xs text-ink-muted italic">{control.evidenceGuidance}</p>
            )}
            <EvidencePanel
              organizationControlId={control.adoption.organizationControlId}
              readOnly={readOnly}
            />
          </div>
        )}
      </Card>
    </li>
  );
}
